import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Shield, Plus, FilePdf, PaperPlaneTilt, Sparkle, FileArrowUp, DotsSixVertical, Trash, ArrowLeft, X } from "@phosphor-icons/react";

const LIST_TYPES = {
  standards: ["standard_id", "title", "effective_date", "impact", "entities"],
  deadlines: ["requirement", "body", "due_date", "action"],
  events: ["date", "title", "host", "location", "agenda"],
  links: ["label", "url", "note"],
};
const AUDIENCE = ["GOP", "GO", "TOP", "TO", "RC", "BA", "DP", "CIP-focused"];

function Aggregator({ sectionType, onParsed }) {
  const ref = useRef(null);
  const [queue, setQueue] = useState([]);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const enqueue = (files) => setQueue((q) => [...q, ...Array.from(files || [])]);

  const parseOne = async (fd) => {
    const { data } = await api.post("/claro-shield/console/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
    onParsed(data.data, data.source);
  };

  const loadAll = async () => {
    if (!queue.length && !url.trim()) { toast.error("Add file(s) or a URL first."); return; }
    setBusy(true);
    let ok = 0;
    for (const f of queue) {
      try { const fd = new FormData(); fd.append("section_type", sectionType); fd.append("file", f); await parseOne(fd); ok++; }
      catch (e) { toast.error(`${f.name}: ${formatApiError(e.response?.data?.detail) || "could not parse"}`); }
    }
    if (url.trim()) {
      try { const fd = new FormData(); fd.append("section_type", sectionType); fd.append("url", url.trim()); await parseOne(fd); ok++; }
      catch (e) { toast.error(`URL: ${formatApiError(e.response?.data?.detail) || "could not parse"}`); }
    }
    setQueue([]); setUrl(""); setBusy(false);
    if (ok) toast.success(`Parsed & filled from ${ok} source${ok > 1 ? "s" : ""}.`);
  };

  return (
    <div className="mt-3 border border-dashed border-border bg-secondary/30 p-3 space-y-2" data-testid={`cs-aggregator-${sectionType}`}
         onDragOver={(e) => e.preventDefault()}
         onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) enqueue(e.dataTransfer.files); }}>
      <p className="text-xs text-muted-foreground">{busy ? "Parsing sources with AI…" : "Stage several PDF/DOCX/CSV/TXT files (drop or add) and/or a URL. Nothing is read until you click Load & fill — AI fills this section & cites each Source."}</p>
      <div className="flex gap-2 flex-wrap items-center">
        <input ref={ref} type="file" multiple className="hidden" accept=".pdf,.docx,.pptx,.xlsx,.csv,.txt,.md,.eml"
               onChange={(e) => { enqueue(e.target.files); e.target.value = ""; }} data-testid={`cs-file-${sectionType}`} />
        <Button size="sm" variant="outline" className="rounded-none" disabled={busy} onClick={() => ref.current?.click()}>
          <FileArrowUp size={14} className="mr-1" /> Add files
        </Button>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…or paste a URL (optional)" className="rounded-none h-9 flex-1 min-w-[200px]" data-testid={`cs-url-${sectionType}`} />
        <Button size="sm" className="rounded-none" disabled={busy || (!queue.length && !url.trim())} onClick={loadAll} data-testid={`cs-load-${sectionType}`}>
          {busy ? "Loading…" : `Load & fill${queue.length ? ` (${queue.length})` : ""}`}
        </Button>
      </div>
      {queue.length > 0 && <div className="flex flex-wrap gap-1">{queue.map((f, i) => (
        <span key={i} className="text-[11px] bg-background border border-border px-2 py-1 flex items-center gap-1 max-w-[220px]" data-testid={`cs-queue-${sectionType}-${i}`}>
          <span className="truncate">{f.name}</span>
          <button onClick={() => setQueue((q) => q.filter((_, x) => x !== i))} disabled={busy}><Trash size={11} /></button>
        </span>))}</div>}
    </div>
  );
}

function ReferenceAdder({ onAdd }) {
  const ref = useRef(null);
  const [queue, setQueue] = useState([]);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const enqueue = (files) => setQueue((q) => [...q, ...Array.from(files || [])]);

  const parseOne = async (fd) => {
    const { data } = await api.post("/claro-shield/console/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
    onAdd({ source: data.data.source, excerpt: data.data.excerpt });
  };

  const loadAll = async () => {
    if (!queue.length && !url.trim()) { toast.error("Add file(s) or a URL first."); return; }
    setBusy(true);
    let ok = 0;
    for (const f of queue) {
      try { const fd = new FormData(); fd.append("section_type", "reference"); fd.append("file", f); await parseOne(fd); ok++; }
      catch (e) { toast.error(`${f.name}: ${formatApiError(e.response?.data?.detail) || "could not read"}`); }
    }
    if (url.trim()) {
      try { const fd = new FormData(); fd.append("section_type", "reference"); fd.append("url", url.trim()); await parseOne(fd); ok++; }
      catch (e) { toast.error(`URL: ${formatApiError(e.response?.data?.detail) || "could not read"}`); }
    }
    setQueue([]); setUrl(""); setBusy(false);
    if (ok) toast.success(`Loaded ${ok} reference${ok > 1 ? "s" : ""}.`);
  };

  return (
    <div className="mt-2 border border-dashed border-border bg-secondary/30 p-3 space-y-2" data-testid="cs-lead-refadder"
         onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) enqueue(e.dataTransfer.files); }}>
      <p className="text-xs text-muted-foreground">Stage reference files (drop several at once, or add more from different places) and/or a URL. Nothing is read until you click <b>Load references</b>.</p>
      <div className="flex gap-2 flex-wrap items-center">
        <input ref={ref} type="file" multiple className="hidden" accept=".pdf,.docx,.pptx,.xlsx,.csv,.txt,.md,.eml" onChange={(e) => { enqueue(e.target.files); e.target.value = ""; }} data-testid="cs-lead-reffile" />
        <Button size="sm" variant="outline" className="rounded-none" disabled={busy} onClick={() => ref.current?.click()}><FileArrowUp size={14} className="mr-1" />Add files</Button>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…or paste a URL (optional)" className="rounded-none h-9 flex-1 min-w-[200px]" data-testid="cs-lead-refurl" />
        <Button size="sm" className="rounded-none" disabled={busy || (!queue.length && !url.trim())} onClick={loadAll} data-testid="cs-lead-refload">
          {busy ? "Loading…" : `Load references${queue.length ? ` (${queue.length})` : ""}`}
        </Button>
      </div>
      {queue.length > 0 && <div className="flex flex-wrap gap-1">{queue.map((f, i) => (
        <span key={i} className="text-[11px] bg-background border border-border px-2 py-1 flex items-center gap-1 max-w-[220px]" data-testid={`cs-lead-queue-${i}`}>
          <span className="truncate">{f.name}</span>
          <button onClick={() => setQueue((q) => q.filter((_, x) => x !== i))} disabled={busy}><X size={11} /></button>
        </span>))}</div>}
    </div>
  );
}

function SectionEditor({ section, ctx, update }) {
  const d = section.data || {};
  const set = (patch) => update({ ...section, data: { ...d, ...patch } });
  const t = section.type;

  const aiSummary = async () => {
    try {
      const { data } = await api.post("/claro-shield/console/ai-draft", { kind: "summary", context: ctx });
      set({ bullets: data.bullets || [] });
      toast.success("Summary drafted.");
    } catch (e) { toast.error("AI summary failed."); }
  };
  const aiLead = async () => {
    try {
      const { data } = await api.post("/claro-shield/console/ai-draft", {
        kind: "lead", prompt: d.brief || "", refs: d.refs || [], existing: d.markdown || "" });
      set({ markdown: data.markdown || "" });
      toast.success("Lead article drafted from your ideas + references.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI draft failed."); }
  };

  if (t === "lead") {
    const refs = d.refs || [];
    return (<>
      <label className="text-xs text-muted-foreground">Ideas / brief for the AI (add as many as you like)</label>
      <Textarea value={d.brief || ""} onChange={(e) => set({ brief: e.target.value })} rows={3} className="rounded-none mt-1" placeholder="Jot down angles, themes, key points — this is combined with any reference material below." data-testid="cs-lead-brief" />
      <ReferenceAdder onAdd={(r) => set({ refs: [...refs, r] })} />
      {refs.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{refs.map((r, i) => (
        <span key={i} className="text-[11px] bg-secondary px-2 py-1 flex items-center gap-1 max-w-[240px]" data-testid={`cs-lead-ref-${i}`}>
          <span className="truncate">{r.source}</span>
          <button onClick={() => set({ refs: refs.filter((_, x) => x !== i) })}><X size={11} /></button>
        </span>))}</div>}
      <div className="flex justify-end my-2"><Button size="sm" className="rounded-none" onClick={aiLead} data-testid="cs-ai-lead"><Sparkle size={14} className="mr-1" />AI draft (ideas + references)</Button></div>
      <label className="text-xs text-muted-foreground">Article (Markdown)</label>
      <Textarea value={d.markdown || ""} onChange={(e) => set({ markdown: e.target.value })} rows={8} className="rounded-none font-mono text-sm mt-1" placeholder="# Editorial spotlight…" data-testid="cs-lead-md" />
      <Input value={d.coauthor || ""} onChange={(e) => set({ coauthor: e.target.value })} placeholder="Co-author (optional)" className="rounded-none mt-2" />
    </>);
  }
  if (t === "summary") {
    const bullets = d.bullets || [];
    return (<>
      <div className="flex justify-end mb-1"><Button size="sm" variant="outline" className="rounded-none" onClick={aiSummary} data-testid="cs-ai-summary"><Sparkle size={14} className="mr-1" />AI auto-summary</Button></div>
      {bullets.map((b, i) => (
        <div key={i} className="flex gap-1 mb-1">
          <Input value={b} onChange={(e) => { const n = [...bullets]; n[i] = e.target.value; set({ bullets: n }); }} className="rounded-none" />
          <Button size="sm" variant="ghost" onClick={() => set({ bullets: bullets.filter((_, x) => x !== i) })}><Trash size={14} /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" className="rounded-none" onClick={() => set({ bullets: [...bullets, ""] })}><Plus size={14} className="mr-1" />Bullet</Button>
    </>);
  }
  if (t === "tips") {
    return (<>
      <label className="text-xs text-muted-foreground">Operator/Auditor Tip of the Month</label>
      <Textarea value={d.operator_tip || ""} onChange={(e) => set({ operator_tip: e.target.value })} rows={2} className="rounded-none mb-2" />
      <label className="text-xs text-muted-foreground">Grid Fun Fact / History Highlight</label>
      <Textarea value={d.fun_fact || ""} onChange={(e) => set({ fun_fact: e.target.value })} rows={2} className="rounded-none" />
    </>);
  }
  // list types
  const cols = LIST_TYPES[t] || [];
  const items = d.items || [];
  const setItem = (i, k, v) => { const n = items.map((it, x) => x === i ? { ...it, [k]: v } : it); set({ items: n }); };
  return (<>
    {items.map((it, i) => (
      <div key={i} className="border border-border p-2 mb-2 space-y-1" data-testid={`cs-item-${t}-${i}`}>
        {cols.map((c) => (
          <Input key={c} value={it[c] || ""} onChange={(e) => setItem(i, c, e.target.value)} placeholder={c.replace(/_/g, " ")} className="rounded-none h-8 text-sm" />
        ))}
        <div className="flex justify-between items-center">
          <span className="text-[11px] text-muted-foreground truncate">Source: {it.source || "—"}</span>
          <Button size="sm" variant="ghost" onClick={() => set({ items: items.filter((_, x) => x !== i) })}><Trash size={14} /></Button>
        </div>
      </div>
    ))}
    <Button size="sm" variant="outline" className="rounded-none" onClick={() => set({ items: [...items, {}] })}><Plus size={14} className="mr-1" />Add row</Button>
  </>);
}

export default function ConsoleClaroShield() {
  const [issues, setIssues] = useState([]);
  const [issue, setIssue] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const saveTimer = useRef(null);

  // Prevent an off-target file drop from making the browser open the file and
  // unload the SPA (which showed as a blank white page). Zone drop handlers still fire.
  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    window.addEventListener("dragover", prevent);
    window.addEventListener("drop", prevent);
    return () => { window.removeEventListener("dragover", prevent); window.removeEventListener("drop", prevent); };
  }, []);

  const loadList = useCallback(async () => {
    const { data } = await api.get("/claro-shield/console");
    setIssues(data.issues || []);
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const save = useCallback((next) => {
    setIssue(next);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(`/claro-shield/console/${next.id}`, {
          title: next.title, volume: next.volume, issue_number: next.issue_number,
          publication_date: next.publication_date, audience_tags: next.audience_tags, sections: next.sections,
        });
      } catch (e) { /* silent autosave */ }
    }, 700);
  }, []);

  const create = async () => {
    const { data } = await api.post("/claro-shield/console", {});
    await loadList(); setIssue(data);
  };
  const open = async (id) => { const { data } = await api.get(`/claro-shield/console/${id}`); setIssue(data); };
  const updateSection = (sec) => save({ ...issue, sections: issue.sections.map((s) => s.id === sec.id ? sec : s) });
  const ctxForSummary = () => issue.sections.filter((s) => s.type !== "summary").map((s) => ({ title: s.title, data: s.data }));

  const reorder = (from, to) => {
    if (from === to || from == null) return;
    const arr = [...issue.sections];
    const [m] = arr.splice(from, 1); arr.splice(to, 0, m);
    save({ ...issue, sections: arr.map((s, i) => ({ ...s, order: i })) });
  };

  const publish = async () => {
    await api.post(`/claro-shield/console/${issue.id}/publish`);
    toast.success("Broadcast published to all customer dashboards.");
    await open(issue.id); await loadList();
  };
  const unpublish = async () => { await api.post(`/claro-shield/console/${issue.id}/unpublish`); await open(issue.id); await loadList(); };
  const downloadPdf = async () => {
    const resp = await api.get(`/claro-shield/console/${issue.id}/pdf`, { responseType: "blob" });
    const u = URL.createObjectURL(resp.data); const a = document.createElement("a"); a.href = u; a.download = "claro-shield.pdf"; a.click(); setTimeout(() => URL.revokeObjectURL(u), 1000);
  };

  if (!issue) {
    return (
      <div className="p-6 max-w-4xl" data-testid="cs-list-page">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-2xl font-bold flex items-center gap-2"><Shield size={24} weight="fill" /> The Claro Shield</h1>
          <Button className="rounded-none" onClick={create} data-testid="cs-create"><Plus size={16} className="mr-1" />New issue</Button>
        </div>
        <div className="space-y-2">
          {issues.length === 0 && <p className="text-sm text-muted-foreground">No newsletters yet. Create your first issue.</p>}
          {issues.map((i) => (
            <button key={i.id} onClick={() => open(i.id)} className="w-full text-left border border-border p-3 hover:bg-secondary/40 flex justify-between" data-testid={`cs-issue-${i.id}`}>
              <span>{i.title} {i.volume && `· Vol ${i.volume}`} {i.issue_number && `· Issue ${i.issue_number}`}</span>
              <span className={`text-xs px-2 py-0.5 ${i.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{i.status}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl" data-testid="cs-editor-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <Button variant="ghost" className="rounded-none" onClick={() => { setIssue(null); loadList(); }}><ArrowLeft size={16} className="mr-1" />All issues</Button>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-none" onClick={downloadPdf} data-testid="cs-pdf"><FilePdf size={16} className="mr-1" />PDF</Button>
          {issue.status === "published"
            ? <Button variant="outline" className="rounded-none" onClick={unpublish}>Unpublish</Button>
            : <Button className="rounded-none" onClick={publish} data-testid="cs-publish"><PaperPlaneTilt size={16} className="mr-1" />Broadcast</Button>}
        </div>
      </div>

      <div className="border border-border p-4 mb-4 space-y-2" data-testid="cs-meta">
        <div className="grid grid-cols-2 gap-2">
          <Input value={issue.title} onChange={(e) => save({ ...issue, title: e.target.value })} placeholder="Title" className="rounded-none" />
          <Input value={issue.publication_date || ""} onChange={(e) => save({ ...issue, publication_date: e.target.value })} placeholder="Publication date (YYYY-MM-DD)" className="rounded-none" />
          <Input value={issue.volume || ""} onChange={(e) => save({ ...issue, volume: e.target.value })} placeholder="Volume" className="rounded-none" />
          <Input value={issue.issue_number || ""} onChange={(e) => save({ ...issue, issue_number: e.target.value })} placeholder="Issue number" className="rounded-none" />
        </div>
        <div className="flex flex-wrap gap-1 pt-1">
          {AUDIENCE.map((a) => {
            const on = (issue.audience_tags || []).includes(a);
            return <button key={a} onClick={() => save({ ...issue, audience_tags: on ? issue.audience_tags.filter((x) => x !== a) : [...(issue.audience_tags || []), a] })}
              className={`text-xs px-2 py-1 border ${on ? "bg-primary text-primary-foreground" : "border-border"}`} data-testid={`cs-tag-${a}`}>{a}</button>;
          })}
        </div>
      </div>

      <div className="space-y-3">
        {[...issue.sections].sort((a, b) => a.order - b.order).map((sec, idx) => (
          <div key={sec.id} className="border border-border" draggable
               onDragStart={() => setDragIdx(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => reorder(dragIdx, idx)}
               data-testid={`cs-section-${sec.type}`}>
            <div className="flex items-center gap-2 bg-secondary/40 px-3 py-2 cursor-move">
              <DotsSixVertical size={16} className="text-muted-foreground" />
              <Input value={sec.title} onChange={(e) => updateSection({ ...sec, title: e.target.value })} className="rounded-none border-0 bg-transparent font-semibold h-7" />
            </div>
            <div className="p-3">
              <SectionEditor section={sec} ctx={ctxForSummary()} update={updateSection} />
              {sec.type !== "tips" && sec.type !== "summary" && sec.type !== "lead" && <Aggregator sectionType={sec.type} onParsed={(data, source) => {
                const merged = { ...(sec.data || {}) };
                if (Array.isArray(data.items)) merged.items = [...(merged.items || []), ...data.items];
                else Object.assign(merged, data);
                updateSection({ ...sec, data: merged, sources: [...(sec.sources || []), source] });
              }} />}
              {sec.type === "summary" && <Aggregator sectionType={sec.type} onParsed={(data) => updateSection({ ...sec, data: { ...(sec.data || {}), ...data } })} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
